import { CreditCardIcon, EnvelopeIcon } from "@heroicons/react/24/solid"
import React, { useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../components/shared/Buttons"

import { canProcessRegistrationPayment } from "./bookableEventPaymentStatus"
import MarkRegistrationPaidModal from "./MarkRegistrationPaidModal"
import {
  MARK_BOOKABLE_EVENT_REGISTRATION_PAID_MUTATION,
  MARK_BOOKABLE_EVENT_SERIES_REGISTRATION_PAID_MUTATION,
  UNDO_MARK_BOOKABLE_EVENT_REGISTRATION_PAID_MUTATION,
  UNDO_MARK_BOOKABLE_EVENT_SERIES_REGISTRATION_PAID_MUTATION
} from "./mutations"

const formatPaidBy = (paidBy) => {
  if (!paidBy) return "offline"
  return paidBy.charAt(0).toUpperCase() + paidBy.slice(1)
}

export default function RegistrationPaymentActions({
  registration,
  practice,
  isSeries = false,
  onPaymentUpdated,
  onShowPaymentModal,
  onShowRequestPaymentModal
}) {
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false)

  const markPaidMutation = isSeries
    ? MARK_BOOKABLE_EVENT_SERIES_REGISTRATION_PAID_MUTATION
    : MARK_BOOKABLE_EVENT_REGISTRATION_PAID_MUTATION
  const undoMarkPaidMutation = isSeries
    ? UNDO_MARK_BOOKABLE_EVENT_SERIES_REGISTRATION_PAID_MUTATION
    : UNDO_MARK_BOOKABLE_EVENT_REGISTRATION_PAID_MUTATION
  const markPaidField = isSeries ? "markBookableEventSeriesRegistrationPaid" : "markBookableEventRegistrationPaid"
  const undoMarkPaidField = isSeries
    ? "undoMarkBookableEventSeriesRegistrationPaid"
    : "undoMarkBookableEventRegistrationPaid"

  const [{ fetching: markingPaid }, markPaid] = useMutation(markPaidMutation)
  const [{ fetching: undoingMarkPaid }, undoMarkPaid] = useMutation(undoMarkPaidMutation)

  if (!registration || registration.amountCents === 0) return null

  const canProcessPayment = canProcessRegistrationPayment(registration)
  const showOfflineActions = canProcessPayment || registration.markPaid

  const handleMarkPaid = async (paidBy) => {
    const result = await markPaid({
      registrationId: registration.id,
      paidBy: paidBy || undefined
    })

    if (result.data?.[markPaidField]?.result === "success") {
      setShowMarkPaidModal(false)
      onPaymentUpdated?.()
    }
  }

  const handleUndoMarkPaid = async () => {
    const result = await undoMarkPaid({ registrationId: registration.id })

    if (result.data?.[undoMarkPaidField]?.result === "success") {
      onPaymentUpdated?.()
    }
  }

  if (!showOfflineActions) return null

  return (
    <>
      <div className="mt-4 space-y-2">
        {registration.markPaid ? (
          <div className="rounded-lg border border-gray-light p-3">
            <span className="text-sm text-teal">Marked as Paid</span>
            {registration.paidBy && (
              <div className="text-sm text-gray-dark">Paid via {formatPaidBy(registration.paidBy)}</div>
            )}
            <Button
              type="link"
              className="mt-1 text-sm text-gray-dark"
              onClick={handleUndoMarkPaid}
              disabled={undoingMarkPaid}>
              {undoingMarkPaid ? "Undoing..." : "Undo"}
            </Button>
          </div>
        ) : (
          <Button type="secondary" className="w-full" onClick={() => setShowMarkPaidModal(true)}>
            Mark as Paid
          </Button>
        )}

        {canProcessPayment && practice.hasPaymentProvider && (
          <>
            <Button type="secondary" className="flex w-full items-center justify-center" onClick={onShowPaymentModal}>
              <CreditCardIcon className="mr-2 size-5" />
              Process Payment
            </Button>
            <Button
              type="tertiary"
              className="flex w-full items-center justify-center"
              onClick={onShowRequestPaymentModal}>
              <EnvelopeIcon className="mr-2 size-5" />
              Request Payment
            </Button>
          </>
        )}

        {canProcessPayment && !practice.hasPaymentProvider && (
          <div className="rounded bg-gray-ultralight p-3">
            <p className="text-sm text-gray-dark">
              To charge a card through Heal.me, connect a payment provider. You can still mark offline payments as paid
              above.{" "}
              <a href="/portal/settings/banking" className="text-teal underline">
                Connect payment provider
              </a>
            </p>
          </div>
        )}
      </div>

      <MarkRegistrationPaidModal
        visible={showMarkPaidModal}
        hideModal={() => setShowMarkPaidModal(false)}
        onSave={handleMarkPaid}
        saving={markingPaid}
      />
    </>
  )
}
